import { useEffect, useState } from "react";
import { Sparkles, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { dictionary, Language } from "../../../utils/dictionary";
import { adsSpyService } from "../../../feature/ads/spy/services/adsSpy.service";
import type { SpyAd } from "../../../feature/ads/spy/types/adsSpy.types";
import ThumbnailWithPlaceholder from "./ThumbnailWithPlaceholder";
import type { CreativeMetric } from "./CreativeCard";

/** Cost metrics don't apply to competitors (we don't know their spend) — fall back to hook_rate. */
type DisplayableMetric = "hook_rate" | "ctr";
const resolveDisplayMetric = (metric?: CreativeMetric): DisplayableMetric =>
  metric === "ctr" ? "ctr" : "hook_rate";

interface CompetitorSidebarProps {
  language: Language;
  limit?: number;
  /** Metric to display on each competitor card — mirrors the user's grid sort. */
  metric?: CreativeMetric;
}

export const CompetitorSidebar = ({ language, limit = 3, metric }: CompetitorSidebarProps) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState<SpyAd[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adsSpyService
      .getAds({ page: 1, limit })
      .then(({ data }) => {
        if (!cancelled) setAds(data);
      })
      .catch(() => {
        if (!cancelled) setAds([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  const handleCreate = (ad: SpyAd) => {
    navigate(`/ads?tab=spy&spy_ad=${ad.id}`);
  };

  return (
    <aside
      className="rounded-2xl p-4 border sticky top-4"
      style={{
        background: "linear-gradient(180deg, #EBEEFF, #F2F4FF, #F8F5FF)",
        borderColor: "#DDE3FF",
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-white text-fluxi text-[11px] font-semibold">
          <Star className="w-3 h-3" />
          {dictionary["performance-competitors-pill"]?.[language] ?? "Espionaje · 48h"}
        </span>
      </div>
      <h3 className="text-sm font-semibold text-gray-900 mb-3 tracking-tight">
        {dictionary["performance-competitors-title"]?.[language] ?? "Ganadores de tu nicho"}
      </h3>

      {loading && <SidebarSkeleton />}

      {!loading && ads.length === 0 && (
        <p className="text-xs text-gray-500 bg-white rounded-xl p-3 border border-white">
          {dictionary["performance-competitors-empty"]?.[language] ?? "Guardá brands en el espía"}
        </p>
      )}

      {!loading && ads.length > 0 && (
        <ul className="space-y-3">
          {ads.map((ad, i) => (
            <li key={ad.id}>
              <CompetitorCard
                ad={ad}
                rank={i + 1}
                language={language}
                displayMetric={resolveDisplayMetric(metric)}
                onCreate={() => handleCreate(ad)}
              />
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
};

const formatDisplayMetric = (ad: SpyAd, metric: DisplayableMetric): string | null => {
  // hook_rate / ctr aren't in the base SpyAd type — the spy provider may enrich
  // ads with them later. When absent, the metric row is simply hidden.
  const value = (ad as SpyAd & { hook_rate?: number; ctr?: number })[metric];
  if (value == null) return null;
  return metric === "hook_rate" ? (value * 100).toFixed(1) + "%" : value.toFixed(2) + "%";
};

const CompetitorCard = ({
  ad,
  rank,
  language,
  displayMetric,
  onCreate,
}: {
  ad: SpyAd;
  rank: number;
  language: Language;
  displayMetric: DisplayableMetric;
  onCreate: () => void;
}) => {
  const thumb = ad.thumbnail_url ?? ad.image_url;
  const metricValue = formatDisplayMetric(ad, displayMetric);
  const metricLabelKey = displayMetric === "hook_rate" ? "performance-metric-hook-rate" : "performance-metric-ctr";
  const metricLabel = dictionary[metricLabelKey]?.[language] ?? displayMetric;

  return (
    <div className="bg-white rounded-xl p-3 border border-white hover:border-fluxi/40 hover:shadow-[0_4px_20px_rgba(67,104,229,0.12)] transition-all">
      <div className="flex gap-3 items-stretch">
        <ThumbnailWithPlaceholder
          src={thumb}
          alt={ad.headline || ad.brand_name}
          format={ad.media_type}
          className="flex-shrink-0 rounded-lg"
          style={{ width: 64, height: 80 }}
          iconClass="w-6 h-6"
        />
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="inline-flex items-center justify-center h-4 px-1.5 rounded bg-gray-100 text-gray-700 text-[10px] font-semibold">
                #{rank}
              </span>
              <span className="text-[10px] text-gray-500 truncate">@{ad.brand_name}</span>
            </div>
            <p className="text-[11px] font-semibold text-gray-900 leading-tight truncate">
              {ad.headline || ad.ad_copy || ad.brand_name}
            </p>
            {metricValue && (
              <div className="flex items-baseline gap-1.5 mt-0.5">
                <span className="text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                  {metricLabel}
                </span>
                <span className="text-[13px] font-semibold text-gray-900 tabular-nums leading-none">
                  {metricValue}
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onCreate}
            className="w-full h-6 inline-flex items-center justify-center gap-1 rounded-md bg-fluxi text-white text-[10px] font-semibold hover:brightness-110 transition-all"
          >
            <Sparkles className="w-2.5 h-2.5" />
            {dictionary["performance-cta-create"]?.[language] ?? "Crear con mi producto"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SidebarSkeleton = () => (
  <ul className="space-y-3">
    {[0, 1, 2].map((i) => (
      <li key={i} className="bg-white rounded-xl p-3 border border-white animate-pulse">
        <div className="flex gap-3 items-stretch">
          <div className="flex-shrink-0 bg-gray-200 rounded-lg" style={{ width: 64, height: 80 }} />
          <div className="flex-1 flex flex-col justify-between">
            <div className="space-y-1.5">
              <div className="h-2 bg-gray-200 rounded w-12" />
              <div className="h-2.5 bg-gray-200 rounded w-full" />
              <div className="h-2.5 bg-gray-200 rounded w-2/3" />
            </div>
            <div className="h-6 bg-gray-200 rounded" />
          </div>
        </div>
      </li>
    ))}
  </ul>
);

export default CompetitorSidebar;
